import { Button, DatePicker, Icons, List, Table } from '@pankod/refine-antd'
import { ExportToExcelButton } from 'components/ExportToExcelButton/ExportToExcelButton'
import { DateFilterDropdown, QuantityFilterDropdown, SelectFilterDropdown } from 'components/filterDropdown'
import { Refund, RefundsListProps } from 'interfaces'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { currency, filter, getRefunds } from 'utility'

const select = 'id, refund_date, refund_number, created_at, subtotal, total, order_id, ship_user_name, ship_street, ship_pob, ship_phone_number, bill_user_name, bill_street, bill_pob, bill_phone_number, bill_nif, refund_items, reviewed'

export type tablePropsProps = {
  dataSource: any[]
}

export const ReviewedCorrectionsList: React.FC<RefundsListProps> = ({ columns, dataToExport }) => {
  let callingServer = false

  const { t } = useTranslation()

  const currentDate = new Date()
  const maxCurrentDate = new Date()
  maxCurrentDate.setMonth(maxCurrentDate.getMonth() - 3)

  const [loading, setLoading] = useState<boolean>(false)
  const [dates, setDates] = useState<any[]>([
    `${maxCurrentDate.getFullYear()}-${(maxCurrentDate.getMonth() + 1 > 9) ? maxCurrentDate.getMonth() + 1 : '0' + (maxCurrentDate.getMonth() + 1)}-${(maxCurrentDate.getDate() > 9) ? maxCurrentDate.getDate() : '0' + maxCurrentDate.getDate()}`,
    `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1 > 9) ? currentDate.getMonth() + 1 : '0' + (currentDate.getMonth() + 1)}-${(currentDate.getDate() > 9) ? currentDate.getDate() : '0' + currentDate.getDate()}`
  ])

  const [tableProps, setTableProps] = useState<tablePropsProps>({ dataSource: [] })
  const [allRows, setAllRows] = useState<any>({ dataSource: [] })
  const [current, setCurrent] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)

  // eslint-disable-next-line no-unused-vars
  const [filters, setFilters] = useState<filter[]>([])

  const getRefundsList = async () => {
    callingServer = true
    setLoading(true)
    const gtDate = dates[0] || maxCurrentDate
    const ltDate = dates[1] || currentDate
    const data = await getRefunds(select, true, gtDate, ltDate, true)
    const tableProps = {
      dataSource: data
    }

    tableProps.dataSource = tableProps.dataSource?.filter((row) => row.reviewed)
    tableProps.dataSource = tableProps.dataSource?.filter((row: any) => !row.disabled)

    tableProps.dataSource = tableProps.dataSource?.map((row: any) => ({
      ...row,
      refund_items: Array.isArray(row.refund_items) ? row.refund_items.join(', ') : row.refund_items?.toString()
    }))

    tableProps.dataSource.forEach((d: Refund, index: number) => {
      d.refundId = index
    })

    setAllRows(tableProps)
    setTableProps(tableProps)
    setLoading(false)
    callingServer = false
  }

  useEffect(() => {
    if (!callingServer) {
      getRefundsList()
    }
  }, [])

  const { RangePicker } = DatePicker

  const currencyRight = (value: any) => <div style={{ textAlign: 'right' }}>{currency(value)}</div>

  let controlWidth = window.innerWidth
  // const fixedMobile = controlWidth > 700 ? 'left' : false
  controlWidth = controlWidth < 728 ? 400 : 105

  const changeDates = (datesInput: any) => {
    if (datesInput === null) {
      setDates([
        `${maxCurrentDate.getFullYear()}-${(maxCurrentDate.getMonth() + 1 > 9) ? maxCurrentDate.getMonth() + 1 : '0' + (maxCurrentDate.getMonth() + 1)}-${(maxCurrentDate.getDate() > 9) ? maxCurrentDate.getDate() : '0' + maxCurrentDate.getDate()}`,
        `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1 > 9) ? currentDate.getMonth() + 1 : '0' + (currentDate.getMonth() + 1)}-${(currentDate.getDate() > 9) ? currentDate.getDate() : '0' + currentDate.getDate()}`
      ])
    }
    const arrayDates = [datesInput[0], datesInput[1]]
    const dateStart = `${arrayDates[0].get('year')}-${(arrayDates[0].get('month') + 1 > 9) ? arrayDates[0].get('month') + 1 : '0' + (arrayDates[0].get('month') + 1)}-${(arrayDates[0].get('date') > 9) ? arrayDates[0].get('date') : '0' + arrayDates[0].get('date')}`
    const dateEnd = `${arrayDates[1].get('year')}-${(arrayDates[1].get('month') + 1 > 9) ? arrayDates[1].get('month') + 1 : '0' + (arrayDates[1].get('month') + 1)}-${(arrayDates[1].get('date') > 9) ? arrayDates[1].get('date') : '0' + arrayDates[1].get('date')}`
    setDates([dateStart, dateEnd])
  }

  return (
    <List
      title={t('menu.refunds')}
      headerProps={{
        extra: <>
          <RangePicker showTime={false} onChange={changeDates} />
          <Button icon={<Icons.SearchOutlined />} onClick={async () => getRefundsList()}>{}</Button>
          <ExportToExcelButton filename={t('menu.refunds')} jsonData={dataToExport(tableProps.dataSource as any[], true)} />
        </>
      }}
    >
      <Table {...tableProps}
        loading={loading}
        pagination={{
          current: current,
          pageSize: pageSize,
          onChange (page, pageSize) {
            setCurrent(page)
            setPageSize(pageSize)
          }
        }}
        scroll={{ x: `${controlWidth}%`, y: '60vh' }}
        rowKey='refundId'>
        {columns.map((column) => {
          return (
            <>
              <Table.Column
                key={column?.key || column?.type}
                title={column?.title}
                dataIndex={column?.dataIndex}
                defaultSortOrder={column?.key === 'refund_date' ? 'descend' : null}
                sorter={{
                  compare: (a: any, b: any) => {
                    const valueA = a[column.dataIndex || '']
                    const valueB = b[column.dataIndex || '']

                    if (typeof valueA === 'number' && typeof valueB === 'number') {
                      return valueA - valueB
                    }

                    return valueA.localeCompare(valueB)
                  }
                }}
                render={(value, record: any) => {
                  if (column.dataIndex && ['subtotal', 'total'].includes(column.dataIndex)) {
                    return currencyRight(value)
                  }

                  return value
                }}
                {...(column?.filterType && {
                  filterDropdown: (props) => {
                    if (column.filterType === 'date') {
                      return <DateFilterDropdown field={column?.key || ''} filters={filters} setFilters={(newFilter: any, method: string) => {
                        if (newFilter.length > 0 && method === 'merge') {
                          console.log(newFilter)
                          const fields = newFilter[0].field?.split('.')
                          const rows = { ...allRows }

                          if (fields?.length === 1) {
                            rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`] >= newFilter[0].value && x[`${fields[0]}`] <= newFilter[1].value)
                          } else if (fields?.length === 2) {
                            rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`][`${fields[1]}`] >= newFilter[0].value && x[`${fields[0]}`][`${fields[1]}`] <= newFilter[1].value)
                          }

                          setTableProps({ ...rows })
                        } else {
                          setTableProps({ ...allRows })
                        }
                      }} />
                    }

                    if (column.filterType === 'number') {
                      return <QuantityFilterDropdown field={column?.key || ''} filters={filters} setFilters={(newFilter: any) => {
                        if (newFilter.length > 0 && Array.isArray(newFilter)) {
                          const fields = newFilter[0].field?.split('.')
                          const rows = { ...allRows }
                          console.log(rows, newFilter)
                          if (fields?.length === 1) {
                            if (newFilter[0].operator === 'eq') {
                              rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`] === parseInt(newFilter[0].value))
                            } else if (newFilter[0].operator === 'gt') {
                              rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`] > parseInt(newFilter[0].value))
                            } else if (newFilter[0].operator === 'lt') {
                              rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`] < parseInt(newFilter[0].value))
                            }
                          } else if (fields?.length === 2) {
                            if (newFilter[0].operator === 'eq') {
                              rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`][`${fields[1]}`] === parseInt(newFilter[0].value))
                            } else if (newFilter[0].operator === 'gt') {
                              rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`][`${fields[1]}`] > parseInt(newFilter[0].value))
                            } else if (newFilter[0].operator === 'lt') {
                              rows.dataSource = rows.dataSource.filter((x: any) => x[`${fields[0]}`][`${fields[1]}`] < parseInt(newFilter[0].value))
                            }
                          }
                          setTableProps({ ...rows })
                        } else {
                          setTableProps({ ...allRows })
                        }
                      }} />
                    }

                    if (column.filterType === 'select') {
                      return (
                        <SelectFilterDropdown
                          props={{
                            ...props,
                            clearFilters: () => {
                              props.clearFilters?.()
                              setTableProps({ ...allRows })
                              props.confirm?.()
                            },
                            confirm: () => {
                              const selected = props.selectedKeys || []
                              const field = column?.dataIndex || column?.key || column?.label || ''
                              const fields = field.toString().split('.')
                              const rows = { ...allRows }

                              if (selected.length > 0) {
                                if (fields.length === 1) {
                                  rows.dataSource = rows.dataSource.filter((x: any) => selected.map(String).includes(String(x[fields[0]])))
                                } else if (fields.length === 2) {
                                  rows.dataSource = rows.dataSource.filter((x: any) => selected.map(String).includes(String(x[fields[0]][fields[1]])))
                                }
                              } else {
                                rows.dataSource = allRows.dataSource
                              }

                              setTableProps({ ...rows })
                              props.confirm?.()
                            }
                          }}
                          entity={column?.entity || ''}
                          optionLabel={column?.label || ''}
                          optionValue={column?.value || ''}
                          type={column?.selectType || 'number'}
                          filters={column?.selectFilters || [{ field: column?.label, operator: 'nnull', value: 'null' }]}
                        />
                      )
                    }
                  }
                })}
                width={150}
              />
            </>
          )
        })}
      </Table>
    </List>
  )
}
